import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  GEOPL_API_PROXY_ENDPOINT,
  ALTI_CODING_API_ENDPOINT,
  createGeoplFetcher,
} from '@e-socna/gis';
import { isDefined } from '@e-socna/utils';
import type { AltiCodingResult, LatLonCoordinates } from '@e-socna/gis';

import { environment } from '../../../../environment';

interface Params {
  readonly position: LatLonCoordinates | undefined;
  readonly resource?: string;
  readonly enabled?: boolean;
}

const geoplFetch = createGeoplFetcher('elevation');

export const useAltiCodingQuery = (
  pParams: Params,
): UseQueryResult<AltiCodingResult> => {
  const { fetcher, lon, lat, enabled, resource } = useMemo(() => {
    const lon = pParams.position?.lon.toFixed(6);
    const lat = pParams.position?.lat.toFixed(6);
    const resource = pParams.resource ?? 'ign_rge_alti_wld';

    const enabled = !!(
      (isDefined(pParams.enabled) ? pParams.enabled : true) &&
      lat &&
      lon
    );

    const params = {
      ...(lon && { lon }),
      ...(lat && { lat }),
      resource,
    };

    const queryString = new URLSearchParams(params).toString();

    const url = `${environment.backendUrl}${GEOPL_API_PROXY_ENDPOINT}/${ALTI_CODING_API_ENDPOINT}?${queryString}`;

    return {
      fetcher: () => geoplFetch<AltiCodingResult>(url),
      lon,
      lat,
      enabled,
      resource,
    };
  }, [
    pParams.enabled,
    pParams.position?.lat,
    pParams.position?.lon,
    pParams.resource,
  ]);

  return useQuery({
    queryKey: ['useAltiCodingQuery', lon, lat, resource],
    queryFn: fetcher,
    enabled,
  });
};
